import { BPS_ONE } from './exact';
import {
  PROC_CHANCE_DENOMINATOR,
  PROC_LEVEL_CAP,
  procChanceNumerator,
  procDamageMilli,
} from './proc-lottery';

/**
 * **O Arsenal Ígneo: um míssil ADICIONAL, sorteado uma vez por salva.**
 *
 * ## O que ele é, e o que o separa das duas joias que já existem
 *
 * O Ciclone é um golpe adicional NAQUELE acerto: sorteia por projétil, e um
 * canhão com mais bocas acerta mais vezes. A Bomba SUBSTITUI um projétil da
 * salva por outra entidade: sorteia por salva, e o projétil que ela ocupa deixa
 * de ser um tiro comum.
 *
 * O míssil não faz nem uma coisa nem outra. Ele **nasce ao lado da salva**, sem
 * lhe tocar: nenhum tiro comum é substituído, nenhum muda de velocidade, de arte
 * ou de dano, e o proc que a loteria por projétil tiver dado a cada um deles
 * sobrevive intacto. É uma entidade nova no mundo, e mais nada.
 *
 * A UNIDADE do sorteio, essa, é a mesma da Bomba - **uma vez por salva** - e
 * pela mesma razão que o gate V28G.J escreveu: um canhão de vinte e oito bocas
 * não pode ter vinte e oito vezes a chance de um de uma boca quando o que está
 * em jogo é uma entidade autoritativa com nove segundos de vida. Vinte e oito
 * mísseis por salva não seriam vinte e oito vezes mais forte - seriam outro jogo,
 * e uma sala que os acumulasse.
 *
 * ## Nasce no DISPARO, e não no acerto
 *
 * O contrato do gate é explícito: "o míssil nasce no momento do disparo, sem
 * depender de acerto". Isso torna-o independente de haver alvo à frente, de o
 * tiro passar ao lado e de o mob morrer antes de a bala chegar - e é o que faz a
 * chance por salva significar o que a etiqueta diz.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não sorteia - a rolagem vem feita, do fluxo autoritativo do participante, e
 * quem a compara contra os intervalos disjuntos é `volley-lottery.ts`. Não
 * escolhe alvo, não voa, não colide, não explode e não conhece uma arena. As
 * duas simulações fazem isso com as constantes que este ficheiro declara.
 */

/* -------------------------------------------------------------------------- */
/*  A chance                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O teto de chance do Arsenal Ígneo: **10 %**, nomeado uma vez.
 *
 * É o número que produz, através de {@link missileChanceNumerator} e do mesmo
 * denominador derivado que as outras duas loterias usam, exatamente a tabela que
 * o gate V28G.L declara:
 *
 * | nível | numerador | chance |
 * | --- | --- | --- |
 * | 1 | 1 000 | 0,333333…% |
 * | 6 | 6 000 | 2 % |
 * | 15 | 15 000 | 5 % |
 * | 30 | 30 000 | **10 %**, o teto final |
 *
 * As quatro linhas do contrato saem desta única constante, sem tabela de
 * exceções e sem nenhum caso especial: `1000 x nível / 300000`. O nível 1 é a
 * razão pela qual a chance NUNCA é dividida antes de ser comparada - `1/300` é
 * uma dízima infinita, e comparar `0,00333…` contra um número em vírgula
 * flutuante seria decidir um proc pelo último bit de um `double`.
 *
 * Não há linha abaixo do 30. Um nível 31 é recusado por todas as fronteiras que
 * escrevem um perfil, e a aritmética defensiva devolve-lhe o mesmo que ao 30 -
 * é `procChanceNumerator` que o garante, e não uma verificação escrita aqui.
 */
export const MISSILE_MAX_CHANCE_BPS = 1_000;

/**
 * O numerador desta joia, dado o seu nível somado.
 *
 * Inteiro, nunca dividido, e comparado contra {@link PROC_CHANCE_DENOMINATOR} -
 * o MESMO espaço de rolagem das outras loterias, porque a lei de chance é a
 * mesma lei. Acima de {@link PROC_LEVEL_CAP} devolve sempre o mesmo valor.
 */
export function missileChanceNumerator(levelSum: number): number {
  return procChanceNumerator(MISSILE_MAX_CHANCE_BPS, levelSum);
}

/* -------------------------------------------------------------------------- */
/*  A magnitude                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **O dano do míssil: 90 % do dano-base autoritativo, em qualquer nível.**
 *
 * ## Porque 90 %, e porque não é um número escolhido aqui
 *
 * Porque é o que o orçamento do V28D.2 produz com este teto de chance e **toda**
 * a parcela paga em dano. A Bomba paga 25 % do seu orçamento em CONTROLO - o
 * empurrão - e por isso a sua magnitude é 112,5 % em vez de 150 %. O míssil não
 * empurra, não tem área e não aplica estado nenhum: tudo o que ele entrega é o
 * golpe, portanto `damageShare` é a unidade inteira.
 *
 *     dano = base x 30 x min(nível,30) x 30 x 10000
 *            -------------------------------------  = base x 9/10
 *              1000 x min(nível,30) x 10000
 *
 * Os dois lados da fração crescem juntos com o nível e cancelam-se: a magnitude
 * é **exatamente 90 % ao nível 1 e ao nível 30**, que é o §"somente a chance
 * cresce com o nível; a magnitude permanece 90 %" do contrato, obtido por
 * construção em vez de por uma condição escrita à mão.
 *
 * Uma única divisão inteira, no fim, e a função é a da loteria - não uma cópia
 * dela.
 *
 * `baseMilli` é o dano-base AUTORITATIVO da nave e mais nada: sem a Chama, sem
 * os Corações, sem runas, sem crítico e sem outro proc.
 */
export function missileDamageMilli(baseMilli: bigint, levelSum: bigint): bigint {
  return procDamageMilli(baseMilli, MISSILE_MAX_CHANCE_BPS, levelSum, BPS_ONE);
}

/* -------------------------------------------------------------------------- */
/*  A seleção congelada                                                       */
/* -------------------------------------------------------------------------- */

/**
 * O míssil que uma salva lançou, congelado no nascimento.
 *
 * `damageMilli` é o RESULTADO e nunca a receita, pela mesma lei que congela o
 * `blastMilli` da Bomba e o `cutMilli` do Ciclone: guardar `levelSum` obrigaria a
 * reavaliar a fórmula no impacto, e uma reavaliação responde "quanto vale agora"
 * em vez de "quanto valia quando este míssil saiu". Nove segundos são tempo de
 * sobra para uma skin mudar.
 *
 * `entropy` viaja com ele para dar determinismo a decisões internas sem uma
 * segunda rolagem - a variação da explosão que o cliente desenha, e o desempate
 * de uma seleção de alvo que precise de um - sem que a instantânea tenha de
 * preservar uma rolagem à parte.
 *
 * Nunca carrega conta, skin, socket, nível ou contagem de cópias: o míssil
 * atravessa o fio para seis clientes.
 */
export interface MissileSelection {
  readonly entropy: number;
  readonly damageMilli: bigint;
}

/*
 * **Não há um tipo de fio para esta seleção, e a ausência é deliberada.**
 *
 * A Bomba tem um - `AtomicBombSelectionWire` - porque ela é um OBJETO pendurado
 * num projétil, e um objeto com um `bigint` lá dentro não sobrevive a
 * `JSON.stringify`. O míssil não se pendura em nada: ele é a entidade, e os dois
 * campos desta seleção vivem DIRETAMENTE nela - `entropy` e `damageMilli`.
 *
 * A instantânea de cada modo escreve-os no seu próprio documento, com a
 * magnitude como string decimal pela regra de sempre. Um tipo intermédio aqui
 * seria uma terceira forma da mesma coisa, e a que envelhece é sempre a que
 * ninguém lê.
 */

/* -------------------------------------------------------------------------- */
/*  O voo, em unidades que os dois modos partilham                            */
/* -------------------------------------------------------------------------- */

/**
 * **Nove segundos, e nem um a mais.**
 *
 * O tempo máximo de perseguição do contrato. Ao esgotá-lo o míssil explode e
 * desaparece **sem causar dano** - a explosão é a mesma arte, e a ausência de
 * dano é a diferença.
 *
 * Em milissegundos porque é a unidade que a caça conta; o duelo deriva daqui o
 * seu número de tiques, e a divisão é exacta.
 */
export const MISSILE_LIFETIME_MS = 9_000;

/**
 * A velocidade do míssil, em basis points da do projétil comum **do seu modo**.
 *
 * **60,6 %**, decidido pelo dono em três cortes:
 *
 *     17 000 -> 10 880 -> 6 965 -> 6 060      (-36 %, -36 %, -13 %)
 *
 * Nasceu 170 % e três avaliações manuais acharam-no rápido demais para se ler.
 * Acumulado, é **-64,4 %** da velocidade original.
 *
 * ## Porque nenhum dos cortes é exato
 *
 * Porque a aritmética do duelo os arredonda. A velocidade do duelo é
 * `62 000 x bps / 10 000` - um INTEIRO de mu por tique, porque uma fração seria
 * truncada a cada tique e o míssil andaria menos do que diz. Isso exige um
 * múltiplo de cinco, e cada corte fica no múltiplo mais próximo do alvo:
 * `6 965 x 0,87 = 6 059,55` -> **6 060**, um corte de 12,994 % em vez de 13,000 %.
 *
 * ## O chão, e a que distância dele estamos
 *
 * 37 572 mu por tique são **2,21x** a velocidade de uma nave. Abaixo de 2x a
 * perseguição começa a não fechar contra um alvo que fuja em linha reta, e a
 * prova em `missile-duel.test.ts` guarda esse limite: mais um corte desta ordem
 * fá-la cair, e é isso que ela existe para dizer antes de o jogo o dizer.
 *
 * Em basis points pela mesma razão que a Bomba usa 8 000: um número absoluto
 * deixaria de ser um MÚLTIPLO do tiro no dia em que a velocidade do tiro
 * mudasse, e ninguém iria à procura dele.
 *
 * ## Passa a ser mais LENTO do que um tiro comum, e isso está certo
 *
 * 0,6965x - os projéteis ultrapassam-no. Não é uma regressão porque o míssil
 * nunca perseguiu balas: ele persegue **naves e mobs**, e contra esses continua
 * a ser 2,5x (nave do duelo) e 10x (mob da caça). A perseguição fecha; o que
 * muda é a leitura, que é precisamente o que se pediu.
 */
export const MISSILE_SPEED_BPS = 6_060;

/**
 * **O limite de giro: 240 graus por segundo.**
 *
 * O contrato pede uma trajetória que "curva suavemente" e nomeia este valor como
 * limite INICIAL, explícito - é uma decisão de sensação, sujeita a revisão
 * manual, e é por isso que está aqui em graus por segundo em vez de convertida
 * para radianos por tique em dois sítios diferentes.
 *
 * O que ele proíbe é o que o contrato proíbe: teletransporte, viragem
 * instantânea e pathfinding. Um míssil que possa virar 240 graus num segundo
 * apanha um alvo que se desloque, e desenha um arco a fazê-lo.
 *
 * Cada modo converte-o para a sua própria aritmética - radianos por segundo na
 * caça, uma rotação inteira de passo fixo por tique no duelo - e as duas
 * conversões derivam DESTE número.
 */
export const MISSILE_TURN_DEGREES_PER_SECOND = 240;

/**
 * O tamanho visual do míssil, em basis points do da Bomba Atômica: **70 %**.
 *
 * O §"Tamanho" do contrato, escrito uma vez. Os dois modos multiplicam por ele o
 * raio de corpo da SUA bomba, portanto "70 % da Bomba" continua a ser verdade
 * quando a Bomba mudar de tamanho - e a proporção da silhueta não muda com o
 * zoom porque a escala é uniforme nos dois eixos.
 *
 * Não é uma constante de arte: é a que decide o corpo mecânico contra o qual a
 * hitbox e a arte se dimensionam, nos dois lados.
 */
export const MISSILE_SIZE_BPS = 7_000;

/**
 * A hitbox do míssil, em basis points do seu meio-comprimento: **125 %**.
 *
 * ## A margem, declarada em vez de estimada
 *
 * A silhueta desenhada mede `meio-comprimento` de meio-vão e `0,5334 x` disso de
 * meia-altura (aletas incluídas), portanto o círculo que a CONTÉM tem
 * `sqrt(1 + 0,5334²) = 1,1334 x` o meio-comprimento. Os 125 % dão-lhe essa
 * contenção mais **10 % de folga**, que é a "margem pequena e declarada" do
 * contrato.
 *
 * ## E não é o raio de proximidade da Bomba
 *
 * A Bomba tem um raio de proximidade de `1,4 x` o corpo porque ela **detona
 * perto** - é uma bomba, e aproximar-se basta. O míssil exige colisão FÍSICA
 * com o inimigo, com uma parede ou com o limite, e uma caixa generosa fá-lo-ia
 * explodir ao largo. O número é menor de propósito, e a razão de ele ser menor é
 * a diferença entre as duas entidades.
 *
 * A contenção da silhueta fica provada contra as dimensões reais da textura, e
 * não contra um número copiado para uma prova.
 */
export const MISSILE_HITBOX_BPS = 12_500;

/* -------------------------------------------------------------------------- */
/*  Os fluxos                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os rótulos dos fluxos de SELEÇÃO DE ALVO, versionados como todos os outros.
 *
 * ## Porque um fluxo próprio, por participante e por finalidade
 *
 * Porque o contrato o exige, e a razão é a de sempre: consumir a escolha de alvo
 * de um fluxo partilhado deslocaria tudo o que viesse depois - runas, spawns,
 * temporizador do boss, pets, a rolagem da Bomba - e uma corrida com a mesma
 * semente deixaria de ser a mesma corrida por causa de uma joia.
 *
 * ## E porque este é consumido CONDICIONALMENTE, ao contrário da rolagem
 *
 * Porque ele serve uma única finalidade e mais ninguém o lê. A regra de consumir
 * sempre existe para fluxos cuja sequência OUTRA coisa observa; aqui a única
 * coisa que observa a sequência é a próxima escolha de alvo de um míssil. Um
 * valor tirado quando um míssil nasce, e outro quando um míssil precisa de
 * trocar de alvo, é uma sequência que a instantânea guarda e uma re-simulação
 * reproduz exatamente - e não desloca coisa nenhuma, porque não há nada a
 * jusante para deslocar.
 */
export const PVP_MISSILE_TARGET_RNG_LABEL = 'pvp-jewel-missile-target-v1';
export const HUNT_MISSILE_TARGET_RNG_LABEL = 'hunt-jewel-missile-target-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_MISSILE_TARGET_RNG_LABEL = 'open-world-jewel-missile-target-v1';

/**
 * A escolha UNIFORME de um alvo entre os candidatos, dada uma rolagem.
 *
 * ## Porque a função recebe a rolagem em vez de a fazer
 *
 * Pela mesma razão que a loteria a recebe: quem sorteia é a simulação, com o seu
 * fluxo autoritativo, e uma função que sorteia não pode ser testada com um valor
 * escolhido.
 *
 * ## Porque a lista tem de chegar em ordem CANÓNICA
 *
 * Porque "uniformemente ao acaso" e "determinístico" só são compatíveis quando o
 * índice sorteado significa sempre o mesmo. Quem chama ordena por id - as
 * cadeiras pela ordem canónica do ruleset, os inimigos da caça por id crescente
 * - e é isso que faz o servidor e o cliente chegarem ao mesmo alvo.
 *
 * @param roll um inteiro em `[0, count)`, do fluxo de alvos do participante
 * @returns o índice escolhido, ou `null` quando não há candidato nenhum
 */
export function chooseMissileTargetIndex(count: number, roll: number): number | null {
  if (!Number.isInteger(count) || count <= 0) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= count) {
    throw new RangeError(`missile target roll outside [0, ${count}): ${roll}`);
  }
  return roll;
}
